import { connect } from "react-redux";
import { RootState } from "src/stores";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchReminderByIdService,
  deleteReminderService,
  getReminderActivityLogs,
} from "src/services/reminder.services";
import { Dialog as HeadlessDialog, Transition } from "@headlessui/react";
import { Fragment } from "react";

import Card from "src/components/Card";
import Button from "src/base-components/Button";
import Lucide from "src/base-components/Lucide";
import moment from "moment";
import CreateReminderPanel from "src/themes/Razor/CreateReminderPanel";

interface ReminderDetailProps {}

interface ActivityLog {
  id: string;
  action_type: string;
  description: string;
  user_name: string;
  email: string;
  created_at: string;
}

function ReminderDetail({}: ReminderDetailProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [reminderData, setReminderData] = useState<any>(null);


  console.log();
  

  const statusText = reminderData?.assignments[0]?.status_text;

  console.log(statusText, "statusText ");

  console.log(reminderData, "reminderData");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Fetch reminder details
  const fetchReminderDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await fetchReminderByIdService(id);

      if (response && response.status) {
        setReminderData(response.result);
      } else {
        setError("Failed to fetch reminder details");
      }
    } catch (err: any) {
      console.error("Error fetching reminder details:", err);
      setError("Failed to fetch reminder details");
    } finally {
      setLoading(false);
    }
  };

  // Fetch activity logs
  const fetchActivityLogs = async () => {
    if (!id) return;

    try {
      setActivityLoading(true);
      const response = await getReminderActivityLogs(id);
      const logs = response || [];
      setActivityLogs(logs);
      setTotalPages(Math.ceil(logs.length / itemsPerPage));
    } catch (err: any) {
      console.error("Error fetching activity logs:", err);
    } finally {
      setActivityLoading(false);
    }
  };

  // Pagination logic
  const getCurrentPageLogs = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return activityLogs.slice(startIndex, endIndex);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  useEffect(() => {
    fetchReminderDetails();
    fetchActivityLogs();
  }, [id]);

  const handleEditReminder = () => {
    setShowEditModal(true);
  };

  const handleBackToReminders = () => {
    navigate("/reminders");
  };

  const handleDeleteReminder = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteReminder = async () => {
    if (!id) return;

    try {
      await deleteReminderService(id);

      // Close modal
      setShowDeleteModal(false);

      // Navigate back to reminders list
      navigate("/reminders");

      console.log("Reminder deleted successfully");
    } catch (error) {
      console.error("Error deleting reminder:", error);
      // You might want to show an error toast here
    }
  };

  const cancelDeleteReminder = () => {
    setShowDeleteModal(false);
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      completed: {
        bg: "bg-green-100",
        text: "text-green-700",
        label: "Completed",
      },
      snoozed: {
        bg: "bg-orange-100",
        text: "text-orange-700",
        label: "Snoozed",
      },
      active: {
        bg: "bg-blue-100",
        text: "text-blue-700",
        label: "Active Again",
      },
      created: { bg: "bg-slate-100", text: "text-slate-700", label: "Created" },
      assigned: {
        bg: "bg-purple-100",
        text: "text-purple-700",
        label: "Assigned",
      },
      pending: {
        bg: "bg-yellow-100",
        text: "text-yellow-700",
        label: "Pending",
      },
    };
    const config =
      statusMap[status as keyof typeof statusMap] || statusMap.pending;
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-slate-600">Loading reminder details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
            <div className="flex items-center space-x-2">
              <Lucide icon="AlertCircle" className="w-5 h-5 text-red-600" />
              <span className="text-red-600">{error}</span>
              <Button
                onClick={fetchReminderDetails}
                size="sm"
                variant="outline-secondary"
                className="ml-auto"
              >
                <Lucide icon="RotateCcw" className="w-4 h-4 mr-1" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!reminderData) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <Lucide
              icon="Calendar"
              className="w-12 h-12 text-slate-400 mx-auto mb-3"
            />
            <h3 className="text-lg font-medium text-slate-900 mb-1">
              Reminder not found
            </h3>
            <p className="text-slate-600">
              The requested reminder could not be found.
            </p>
            <Button
              onClick={handleBackToReminders}
              variant="primary"
              className="mt-4"
            >
              Back to Reminders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleBackToReminders}
              variant="outline-secondary"
              size="sm"
            >
              <Lucide icon="ArrowLeft" className="w-4 h-4 mr-2" />
              Reminders
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">
              {reminderData.title}
            </h1>
            <span
              className={`
  ${
    statusText === "Completed"
      ? "text-green-500"
      : statusText === "Overdue"
      ? "text-red-500"
      : "text-slate-500"
  }
`}
            >
              {statusText}
            </span>
          </div>

          {statusText !== "Completed" && (
            <div className="flex gap-3">
              <Button onClick={handleEditReminder} variant="primary">
                <Lucide icon="Pencil" className="w-4 h-4 mr-2" />
                Edit Reminder
              </Button>
              <Button onClick={handleDeleteReminder} variant="danger">
                <Lucide icon="Trash2" className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Reminder Details */}
          <div className="lg:col-span-1 space-y-6">
            {/* Reminder Details Card */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                REMINDER DETAILS
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Title
                  </label>
                  <p className="text-slate-900 mt-1">{reminderData.title}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Description
                  </label>
                  <p className="text-slate-900 mt-1">
                    {reminderData.description}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Remind On
                  </label>
                  <p className="text-slate-900 mt-1">
                    {moment(reminderData.remind_on).format(
                      "DD/MM/YYYY, HH:mm:ss"
                    )}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Remind Before
                  </label>
                  <p className="text-slate-900 mt-1">
                    {reminderData.remind_before_minutes} minutes
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Created For
                  </label>
                  <p className="text-slate-900 mt-1">
                    {reminderData.assigned_users?.length > 0
                      ? reminderData.assigned_users
                          .map((user: any) => `${user.name} ${user.last_name}`)
                          .join(", ")
                      : "Myself"}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Created By
                  </label>
                  <p className="text-slate-900 mt-1">
                    {reminderData.created_by
                      ? `${reminderData.created_by.name} ${
                          reminderData.created_by.last_name || ""
                        }`
                      : "Current User"}
                  </p>
                </div>
              </div>
            </Card>

            {/* Query Information Card */}
            {reminderData.query_record && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        navigate(
                          `/query-detail/${reminderData.query_record.id}`
                        )
                      }
                      className="text-sm font-medium"
                    >
                      {reminderData?.query_record?.query_no || "N/A"}
                    </Button>
                    <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-sm">
                      In-progress
                    </span>
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">
                      Payment In Progress
                    </span>
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">
                      Customer ID:
                    </span>
                    <span className="ml-2">
                      {reminderData?.query_record?.customer?.customer_id ||
                        "N/A"}
                    </span>
                  </div>

                  <div>
                    <span className="font-medium text-slate-700">
                      Customer Name
                    </span>
                    <span className="ml-2">
                      {reminderData?.query_record?.customer?.fullname || "N/A"}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-slate-700">
                        Source:
                      </span>
                      <div className="flex items-center mt-1">
                        <Lucide icon="Phone" className="w-4 h-4 mr-1" />
                        <span>Phone</span>
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">
                        Location:
                      </span>
                      <p className="mt-1">Calgary Office</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-slate-700">Dated:</span>
                      <p className="mt-1">
                        {moment(reminderData.query_record.created_at).format(
                          "DD-MM-YYYY"
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">
                        Last Updated On:
                      </span>
                      <p className="mt-1">
                        {moment(reminderData.query_record.updated_at).format(
                          "DD-MM-YYYY"
                        )}
                      </p>
                    </div>
                  </div>

                  {/* <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-slate-700">
                        Created By:
                      </span>
                      <p className="mt-1">
                        {reminderData.query_record.created_by?.name ||
                          "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">
                        Assigned To:
                      </span>
                      <p className="mt-1">
                        {reminderData.query_record.assigned_to?.name || "N/A"}
                      </p>
                    </div>
                  </div> */}
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Reminder Activity */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-6">
                REMINDER ACTIVITY
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-medium text-slate-700">
                        Date & Time
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-slate-700">
                        Action
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-slate-700">
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLoading ? (
                      <tr>
                        <td colSpan={3} className="py-8 text-center">
                          <div className="flex items-center justify-center">
                            <Lucide
                              icon="Loader2"
                              className="w-5 h-5 animate-spin mr-2"
                            />
                            Loading activity logs...
                          </div>
                        </td>
                      </tr>
                    ) : activityLogs.length > 0 ? (
                      getCurrentPageLogs().map(
                        (log: ActivityLog, index: number) => (
                          <tr
                            key={log.id}
                            className="border-b border-slate-100"
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center">
                                <Lucide
                                  icon="Calendar"
                                  className="w-4 h-4 mr-2 text-slate-500"
                                />
                                {moment(log.created_at).format(
                                  "DD/MM/YYYY, HH:mm:ss"
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              {getStatusBadge(log.action_type)}
                            </td>
                            <td className="py-4 px-4">
                              <div>
                                <p className="text-slate-600">
                                  {log.description}
                                </p>
                                <p className="text-sm text-slate-500 mt-1">
                                  By: {log.user_name} ({log.email})
                                </p>
                              </div>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="py-8 text-center text-slate-500"
                        >
                          No activity logs found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                {activityLogs.length > itemsPerPage && (
                  <div className="flex items-center justify-center mt-6">
                    <div className="flex items-center space-x-1">
                      {/* First Page Button */}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                        className="px-2 py-1 min-w-[32px] h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Lucide icon="ChevronsLeft" className="w-4 h-4" />
                      </Button>

                      {/* Previous Page Button */}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        className="px-2 py-1 min-w-[32px] h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Lucide icon="ChevronLeft" className="w-4 h-4" />
                      </Button>

                      {/* Page Numbers */}
                      {getVisiblePages().map((page, index) => (
                        <React.Fragment key={index}>
                          {page === "..." ? (
                            <span className="px-2 py-1 text-slate-400">
                              ...
                            </span>
                          ) : (
                            <Button
                              variant={
                                currentPage === page
                                  ? "primary"
                                  : "outline-secondary"
                              }
                              size="sm"
                              onClick={() => handlePageChange(page as number)}
                              className={`px-3 py-1 min-w-[32px] h-8 ${
                                currentPage === page
                                  ? "bg-blue-600 text-white"
                                  : "hover:bg-slate-50"
                              }`}
                            >
                              {page}
                            </Button>
                          )}
                        </React.Fragment>
                      ))}

                      {/* Next Page Button */}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                        className="px-2 py-1 min-w-[32px] h-8 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Lucide icon="ChevronRight" className="w-4 h-4" />
                      </Button>

                      {/* Last Page Button */}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        className="px-2 py-1 min-w-[32px] h-8 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Lucide icon="ChevronsRight" className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Reminder Modal */}
      <CreateReminderPanel
        visible={showEditModal}
        setVisible={setShowEditModal}
        onSuccess={() => {
          fetchReminderDetails(); // Refresh the details after successful edit
        }}
        editMode={true}
        editReminderId={id || ""}
        editReminderData={reminderData}
      />

      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <HeadlessDialog
          as="div"
          className="relative z-50"
          onClose={() => setShowDeleteModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessDialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                    <Lucide
                      icon="AlertTriangle"
                      className="w-6 h-6 text-red-600"
                    />
                  </div>

                  <HeadlessDialog.Title className="text-lg font-semibold text-slate-800 text-center mb-2">
                    Delete Reminder
                  </HeadlessDialog.Title>

                  <p className="text-sm text-slate-600 text-center mb-6">
                    Are you sure you want to delete this reminder? This action
                    cannot be undone.
                  </p>

                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={cancelDeleteReminder}
                      className="px-6"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={confirmDeleteReminder}
                      className="px-6"
                    >
                      <Lucide icon="Trash2" className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </HeadlessDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessDialog>
      </Transition>
    </div>
  );
}

const mapStateToProps = ({}: RootState) => ({});
const mapDispatchToProps = (dispatch: any) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(ReminderDetail);
